type EventPostalAddress = {
  streetAddress?: string
  addressLocality?: string
  addressRegion?: string
  postalCode?: string
  addressCountry?: string
}

type PhysicalEventLocation = {type: 'physical'; name?: string; address?: EventPostalAddress}
type VirtualEventLocation = {type: 'virtual'; url: string}

export type EventAttendanceMode = 'in-person' | 'online' | 'hybrid'

export type EventSchemaDetails = {
  startDate?: string
  endDate?: string
  attendanceMode?: EventAttendanceMode
  location?: PhysicalEventLocation | VirtualEventLocation | Array<PhysicalEventLocation | VirtualEventLocation>
}

type EventSeoSchemaProps = EventSchemaDetails & {
  title: string
  description?: string
  imageUrl?: string
  dataTestId?: string
  contributors?: Array<{name: string}>
}

const attendanceModeUrls: Record<EventAttendanceMode, string> = {
  'in-person': 'https://schema.org/OfflineEventAttendanceMode',
  online: 'https://schema.org/OnlineEventAttendanceMode',
  hybrid: 'https://schema.org/MixedEventAttendanceMode',
}

function toStructuredLocation(location: PhysicalEventLocation | VirtualEventLocation) {
  return location.type === 'physical'
    ? {
        '@type': 'Place',
        name: location.name,
        address: location.address ? {'@type': 'PostalAddress', ...location.address} : undefined,
      }
    : {
        '@type': 'VirtualLocation',
        url: location.url,
      }
}

export const EventSeoSchema = ({
  title,
  description,
  imageUrl,
  startDate,
  endDate,
  attendanceMode,
  location,
  dataTestId,
  contributors,
}: EventSeoSchemaProps) => {
  const imageLinks = imageUrl
    ? [
        `https:${imageUrl}?w=2560&h=1440&fm=webp`,
        `https:${imageUrl}?w=1280&h=960&fm=webp`,
        `https:${imageUrl}?w=1000&h=1000&fm=webp`,
      ]
    : undefined
  const structuredLocation = !location
    ? undefined
    : Array.isArray(location)
      ? location.map(toStructuredLocation)
      : toStructuredLocation(location)

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: title,
    description,
    image: imageLinks,
    startDate,
    endDate,
    contributor: contributors && contributors.map(c => ({'@type': 'Person', name: c.name})),
    eventAttendanceMode: attendanceMode ? attendanceModeUrls[attendanceMode] : undefined,
    location: structuredLocation,
    organizer: {
      '@type': 'Organization',
      name: 'GitHub',
      url: 'https://www.github.com',
    },
  }

  const jsonLd = JSON.stringify(structuredData).replace(/</g, '\\u003c')

  /* eslint-disable-next-line react/no-danger */
  return <script data-testid={dataTestId} type="application/ld+json" dangerouslySetInnerHTML={{__html: jsonLd}} />
}
